'use client';

import { createContext, useCallback, useContext, useMemo, useState, type PropsWithChildren } from 'react';
import type { NebulyPageViewState } from '@/domains/pages/types';

type NebulyPageContextValue = {
  viewState: NebulyPageViewState;
  setViewState: (state: NebulyPageViewState) => void;
  titleSubject?: string;
  setTitleSubject: (subject?: string) => void;
};

const NebulyPageContext = createContext<NebulyPageContextValue | null>(null);

function isSameViewState(prev: NebulyPageViewState, next: NebulyPageViewState): boolean {
  if (prev.status !== next.status) return false;

  switch (next.status) {
    case 'loading':
    case 'ready':
      return true;
    case 'empty':
      return prev.status === 'empty' && prev.reason === next.reason;
    case 'error':
      return prev.status === 'error' && prev.error === next.error;
    default:
      return false;
  }
}

export function NebulyPageProvider({ children }: PropsWithChildren) {
  const [viewState, setViewStateInternal] = useState<NebulyPageViewState>({ status: 'loading' });
  const [titleSubject, setTitleSubject] = useState<string | undefined>();

  const setViewState = useCallback((state: NebulyPageViewState) => {
    setViewStateInternal((prev) => (isSameViewState(prev, state) ? prev : state));
  }, []);

  const value = useMemo(
    () => ({ viewState, setViewState, titleSubject, setTitleSubject }),
    [titleSubject, viewState, setViewState],
  );

  return <NebulyPageContext.Provider value={value}>{children}</NebulyPageContext.Provider>;
}

export function useNebulyPageContext() {
  const context = useContext(NebulyPageContext);
  if (!context) {
    throw new Error('useNebulyPageContext must be used within NebulyPageProvider');
  }
  return context;
}
